import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material'
import { FunctionComponent } from 'react'
import { IHeadCell } from '../../../shared/interfaces/table-display.model'
import { ChevronRight, Circle } from '@mui/icons-material'

export const TableDisplayMobile: FunctionComponent<{
  rows: any,
  headCells: IHeadCell[],
  haveCheckboxSelect?: boolean,
  selectByIcon?: boolean,
  selectByCheckboxOnly?: boolean,
  number?: number
  handleSelected?: (selectedRowId: string[]) => void;
  /** When set (e.g. emissions list), row tap runs this instead of only selection. */
  onRowClick?: (id: string) => void;
  mobileCardVariant?: 'default' | 'emissions' | 'locations';
  selected: string[],
  setSelected: React.Dispatch<React.SetStateAction<any>>
}> = (
  {
    selected,
    rows,
    headCells,
    handleSelected,
    onRowClick,
    mobileCardVariant = 'default',
  }) => {

    const isRowSelected = (rowId: string | number) =>
      selected.some((s) => String(s) === String(rowId))

    const generateCardContent = (row: any, headCells: IHeadCell[]) => {
      const cellsToInclude = headCells.filter(cell => cell.includeInMobile !== false)
      const headingCell = cellsToInclude.find(cell => cell.isHeading);
      const subHeadingCell = cellsToInclude.find(cell => cell.isSubHeading)
      const otherCells = cellsToInclude.filter(cell => !cell.isHeading && !cell.isSubHeading && cell.label !== 'spendBasedConversionId');

      return (
        <>
          <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 1 }}>
            {/* Table Heading */}
            {headingCell && row[headingCell.id] &&
              <Typography variant='subtitle1' sx={{ fontWeight: 600, mr: 2 }}>
                {row[headingCell.id]}
              </Typography>
            }
            {/* Table SubHeading */}
            {subHeadingCell && row[subHeadingCell.id] &&
              (subHeadingCell.id === 'locked' ?
                <Circle sx={{ color: row.locked === 'Open' ? 'primary.main' : 'lightGrey.main' }} />
                :
                subHeadingCell.id === 'active' ?
                  <Circle sx={{ color: row.active === 'Yes' ? 'primary.main' : 'lightGrey.main' }} />
                  :
                  <Typography sx={{ fontSize: '0.6rem' }}>
                    {row[subHeadingCell.id]}
                  </Typography>
              )
            }
          </Stack>
          {headCells.find(cell => cell.id === 'spendBasedConversionId') &&
            <Typography variant='body1' sx={{ color: 'text.secondary' }}>{row.spendBasedConversionId}
            </Typography>
          }
          <Grid container spacing={0} >
            {otherCells.map((cell, index) => (
              row[cell.id] && (
                <Grid item xs={(index === otherCells.length - 1 || cell.oneRow) ? 12 : 6} key={index} sx={{ p: 0, mb: 0.5 }}>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    {cell.label === 'Amount (£)'
                      ? `£${row[cell.id]}`
                      : cell.label === 'Emissions (kg CO2e)'
                        ? `${row[cell.id]} kg CO2e`
                        : cell.label === 'Emission per Unit (kg CO2e)'
                          ? `${row[cell.id]} kg CO2e per Unit`
                          : row[cell.id]}
                  </Typography>
                </Grid>
              )
            ))}
          </Grid>
        </>
      );
    };

    const generateLocationsCardContent = (row: any, cells: IHeadCell[]) => {
      const cellsToInclude = cells.filter((cell) => cell.includeInMobile !== false);
      const headingCell = cellsToInclude.find((cell) => cell.isHeading);
      const title = headingCell ? row[headingCell.id] : null;
      const detailCells = cellsToInclude.filter(
        (cell) =>
          !cell.isHeading &&
          !cell.isSubHeading &&
          cell.id !== 'spendBasedConversionId' &&
          row[cell.id] != null &&
          row[cell.id] !== '',
      );

      return (
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1, minWidth: 0, lineHeight: 1.35 }}>
              {title}
            </Typography>
            {(handleSelected || onRowClick) && (
              <ChevronRight sx={{ color: 'text.secondary', flexShrink: 0, fontSize: 22 }} aria-hidden />
            )}
          </Stack>
          {detailCells.map((cell) => (
            <Stack key={String(cell.id)} spacing={0.375}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.03, lineHeight: 1.2 }}
              >
                {cell.label}
              </Typography>
              <Box
                sx={{
                  typography: 'body2',
                  color: 'text.primary',
                  lineHeight: 1.4,
                  '& .MuiStack-root': { alignItems: 'center', flexWrap: 'nowrap' },
                  '& img': { flexShrink: 0, display: 'block' },
                }}
              >
                {typeof row[cell.id] === 'string' || typeof row[cell.id] === 'number' ? (
                  <Typography variant="body2" component="span" sx={{ wordBreak: 'break-word' }}>
                    {row[cell.id]}
                  </Typography>
                ) : (
                  row[cell.id]
                )}
              </Box>
            </Stack>
          ))}
        </Stack>
      );
    };

    const locationsMobileGrid = (
      <Grid container spacing={1.5}>
        {rows.map((row: any) => (
          <Grid item xs={12} md={12} lg={12} key={row.id}>
            <Card
              onClick={() => {
                if (onRowClick) {
                  onRowClick(row.id.toString());
                } else if (handleSelected) {
                  handleSelected([row.id]);
                }
              }}
              variant="outlined"
              sx={{
                cursor: onRowClick || handleSelected ? 'pointer' : 'default',
                borderRadius: 2,
                boxShadow: 'none',
                borderColor: 'divider',
                bgcolor: isRowSelected(row.id) ? 'action.hover' : 'background.paper',
                transition: 'background-color 0.15s ease',
                '&:active': { bgcolor: 'action.selected' },
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {generateLocationsCardContent(row, headCells)}
              </CardContent>
            </Card>
          </Grid>
        ))}
        {rows.length === 0 && (
          <Grid item xs={12} md={12} lg={12}>
            <Card sx={{ cursor: 'default', pt: 1, pl: 1.5, pr: 1.5, pb: 0, backgroundColor: 'common.white' }} variant="outlined">
              <CardContent sx={{ p: 0, margin: 'auto' }}>
                <Typography>No records</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    );

    const emissionsMobileGrid = (
      <Grid container spacing={1.5}>
        {rows.map((row: any) => (
          <Grid item xs={12} md={12} lg={12} key={row.id}>
            <Card
              onClick={() => {
                if (onRowClick) {
                  onRowClick(row.id.toString())
                } else if (handleSelected) {
                  handleSelected([row.id])
                }
              }}
              variant="outlined"
              sx={{
                cursor: onRowClick || handleSelected ? 'pointer' : 'default',
                borderRadius: 2,
                boxShadow: 'none',
                borderColor: 'divider',
                bgcolor: isRowSelected(row.id) ? 'action.hover' : 'background.paper',
                transition: 'background-color 0.15s ease',
                '&:active': { bgcolor: 'action.selected' },
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack spacing={1.75}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          '& .MuiTypography-root': {
                            fontWeight: 500,
                            fontSize: '1.0625rem',
                            lineHeight: 1.35,
                            whiteSpace: 'normal',
                            overflow: 'visible',
                            textOverflow: 'unset',
                            display: 'block',
                          },
                        }}
                      >
                        {row.activityLabel}
                      </Box>
                    </Box>
                    <Box
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'flex-start',
                        mt: -0.5,
                        mr: -0.5,
                      }}
                    >
                      {row.action}
                    </Box>
                  </Stack>

                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    rowGap={1}
                    sx={{ width: '100%', alignSelf: 'stretch' }}
                  >
                    <Box
                      sx={{
                        flex: '0 1 auto',
                        minWidth: 0,
                        '& .MuiTypography-root': {
                          fontWeight: 700,
                          fontSize: '1rem',
                          fontVariantNumeric: 'tabular-nums',
                          textAlign: 'left !important',
                          display: 'block',
                        },
                      }}
                    >
                      {row.totalEmissionsKg}
                    </Box>
                    <Box sx={{ flex: '0 1 auto', display: 'flex', justifyContent: 'flex-start' }}>
                      {row.methodLabel}
                    </Box>
                  </Stack>

                  <Box sx={{ width: '100%' }}>{row.primaryDataPercent}</Box>

                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ pt: 0.25 }}
                  >
                    <Box
                      component="div"
                      sx={{
                        typography: 'caption',
                        color: 'text.secondary',
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      {row.startDate}
                    </Box>
                    {onRowClick && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.25}
                        sx={{ color: 'text.secondary', flexShrink: 0, ml: 1 }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                          View
                        </Typography>
                        <ChevronRight sx={{ fontSize: 20 }} aria-hidden />
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {rows.length === 0 && (
          <Grid item xs={12} md={12} lg={12}>
            <Card sx={{ cursor: 'default', pt: 1, pl: 1.5, pr: 1.5, pb: 0, backgroundColor: 'common.white' }} variant="outlined">
              <CardContent sx={{ p: 0, margin: 'auto' }}>
                <Typography>No records</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    )

    if (mobileCardVariant === 'emissions') {
      return emissionsMobileGrid
    }

    if (mobileCardVariant === 'locations') {
      return locationsMobileGrid
    }

    return (
      <Grid container spacing={1}>
        {rows.length > 0 && rows.map((row: any) => (
          <Grid item xs={12} md={12} lg={12} key={row.id}>
            <Card
              onClick={() => {
                if (onRowClick) {
                  onRowClick(row.id.toString())
                } else if (handleSelected) {
                  handleSelected([row.id])
                }
              }}
              sx={{ cursor: 'pointer', pt: 2, pl: 2, pr: 2, pb: 0, backgroundColor: selected[0] === row.id ? 'lightGrey.main' : 'common.white' }}
              variant='outlined'
            >
              <CardContent sx={{ p: 0 }}>
                {generateCardContent(row, headCells)}
              </CardContent>
            </Card>
          </Grid>
        ))}
        {rows.length === 0 &&
          <Grid item xs={12} md={12} lg={12}>
            <Card sx={{ cursor: 'default', pt: 1, pl: 1.5, pr: 1.5, pb: 0, backgroundColor: 'common.white' }} variant='outlined'>
              <CardContent sx={{ p: 0, margin: 'auto' }}>
                <Typography>No records</Typography>
              </CardContent>
            </Card>
          </Grid>
        }
      </Grid>
    )
  }
